import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Select, MenuItem, TextField, FormControlLabel, Checkbox, Grid, Typography } from '@mui/material';
import * as XLSX from 'xlsx-js-style';
import { uploadCollection } from '../../../api/DynamicCollections';
import { useMessage } from '../../ContextMessage';

// Define available tabs
const TABS = [
    { name: 'plants_varieties', label: 'Plants / Varieties' },
    { name: 'compounds', label: 'Compounds' },
    { name: 'pharmacology', label: 'Pharmacology' },
    { name: 'toxicity', label: 'Toxicity' },
    { name: 'disorders_diceases', label: 'Disorders / Diseases' },
    { name: 'quality_safety', label: 'Quality & Safety' },
    { name: 'products', label: 'Products' },
    { name: 'regulations', label: 'Regulations' },
    { name: 'patents', label: 'Patents' },
    { name: 'references', label: 'References' }
];

const DEFAULT_PRIMARY_KEY_COLUMNS = ['INCHIKEY', 'CAS', 'COMPOUND_NAME', 'SYNONYMS'];

interface UploadCollectionModalProps {
    open: boolean;
    onClose: () => void;
    onUploadSuccess: () => void; // Callback for registry reload
}

const UploadCollectionModal: React.FC<UploadCollectionModalProps> = ({ open, onClose, onUploadSuccess }) => {
    const [file, setFile] = useState<File | null>(null);
    const [collectionName, setCollectionName] = useState('');
    const [tabName, setTabName] = useState('');
    const [primaryKeys, setPrimaryKeys] = useState<string[]>([]);
    const [headers, setHeaders] = useState<string[]>([]);
    const [replace, setReplace] = useState<boolean>(false);
    const { showInfo, showWarning } = useMessage();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        setFile(f || null);
        if (f) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                const bstr = evt.target?.result as string;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const jsonData = XLSX.utils.sheet_to_json(ws, { defval: '' });
                const keys = Object.keys(jsonData[0] || {});
                setHeaders(keys);
                const defaultKeys = keys.filter(key =>
                    DEFAULT_PRIMARY_KEY_COLUMNS.includes(key) || key.toUpperCase().endsWith('_TYPE')
                );
                setPrimaryKeys(defaultKeys);
            };
            reader.readAsBinaryString(f);
        }
    };

    const handleSubmit = async () => {
        if (!file || !collectionName || !tabName) {
            showWarning('Please fill in all required fields.');
            return;
        }

        const reader = new FileReader();
        reader.onload = async (evt) => {
            const bstr = evt.target?.result as string;
            const wb = XLSX.read(bstr, { type: 'binary' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(ws, { defval: '' });

            const payload = {
                collectionName,
                primaryKeyColumns: primaryKeys,
                tabName,
                rows: jsonData,
                replace
            };

            try {
                const res = await uploadCollection(payload);
                showInfo(`Uploaded: ${res.inserted} inserted, ${res.updated} updated.`);
                window.dispatchEvent(new CustomEvent('REGISTER_COLLECTION', { detail: { ...payload, inserted: res.inserted } }));
                onUploadSuccess();
                handleClose();
            } catch (error) {
                console.error('Upload failed:', error);
                showWarning('Failed to upload collection.');
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleClose = () => {
        setFile(null);
        setCollectionName('');
        setTabName('');
        setPrimaryKeys([]);
        setHeaders([]);
        setReplace(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} aria-labelledby="upload-collection-dialog-title" maxWidth="md" fullWidth>
            <DialogTitle id="upload-collection-dialog-title">Upload New Collection</DialogTitle>
            <DialogContent>
                <FormControl fullWidth margin="normal">
                    <TextField
                        label="Collection Name"
                        value={collectionName}
                        onChange={(e) => setCollectionName(e.target.value)}
                        required
                        variant="outlined"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel id="tab-name-label">Tab Name</InputLabel>
                    <Select
                        labelId="tab-name-label"
                        value={tabName}
                        label="Tab Name"
                        onChange={(e) => setTabName(e.target.value)}
                        required
                    >
                        <MenuItem value="">Select Tab Name...</MenuItem>
                        {TABS.map(tab => (
                            <MenuItem key={tab.name} value={tab.name}>
                                {tab.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <input
                        type="file"
                        accept=".xlsx, .xls"
                        onChange={handleFileChange}
                        style={{ marginTop: '16px' }}
                    />
                </FormControl>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={replace}
                            onChange={(e) => setReplace(e.target.checked)}
                        />
                    }
                    label="Replace existing data in collection?"
                    style={{ marginTop: '16px' }}
                />

                {headers.length > 0 && (
                    <>
                        <Typography variant="subtitle1" gutterBottom style={{ marginTop: '16px' }}>
                            <b>SELECT Primary Key Columns</b>
                        </Typography>
                        <Grid container spacing={2}>
                            {headers.map(h => (
                                <Grid item xs={6} key={h}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={primaryKeys.includes(h)}
                                                onChange={() =>
                                                    setPrimaryKeys(pk =>
                                                        pk.includes(h)
                                                            ? pk.filter(x => x !== h)
                                                            : [...pk, h]
                                                    )
                                                }
                                            />
                                        }
                                        label={h}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}


            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    disabled={!file || !collectionName || !tabName}
                >
                    Upload
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UploadCollectionModal;
